import { formatDate } from '@/lib/formatDate';
import React, { memo, useState, useMemo } from "react";
import VisitCard from "@/components/Patient/VisitCard";
import type { VisitRecord } from "@/lib/api";
import { billSummary, billText, readingItems } from "@/lib/vitals";
import { Button } from "@/components/ui/button";
import { Prescription } from "@/types/prescription";
import { Calendar, FileText, Clock, SortDesc, UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import { isValid } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { canEditPrescription } from "@/lib/prescriptionEdit";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Visit {
  id: string;
  date: string;
  prescription?: Prescription;
  appointment?: any;
}

interface VisitTimelineProps {
  visits: Visit[];
  /** Real visits: group prescriptions by visit and show visits without one (e.g. left after vitals) */
  visitRecords?: VisitRecord[];
  /** Patient's age in months, for paediatric H/L flags */
  ageMonths?: number | null;
  onEditPrescription?: (prescription: Prescription) => void;
  onViewPrescription?: (prescription: Prescription) => void;
  onCopyPrescription?: (prescription: Prescription) => void;
  loading?: boolean;
  /** Below the prescription pad: only what helps write today's prescription (no counts, sort, labels or bills) */
  prescribing?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseDate = (dateString: string): Date | null => {
  try {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

/** What a visit without a prescription amounted to */
const noPrescriptionText = (v: VisitRecord): string => {
  if (v.status === "cancelled") return "Left without being seen";
  if (v.status === "auto_closed") return "Not completed";
  if (v.status === "waiting" || v.status === "in_consultation" || v.status === "on_hold") return "In progress";
  return "Seen, no prescription";
};

/** One row of the timeline: a prescription (with its visit when known), or a visit with no prescription */
type TimelineItem =
  | { kind: "rx"; key: string; date: string; prescription: Prescription; visit?: VisitRecord }
  | { kind: "visit"; key: string; date: string; visit: VisitRecord };

const isValidPrescription = (
    prescription: Prescription | undefined
): prescription is Prescription =>
    !!(prescription?.id && prescription?.date && parseDate(prescription.date));

// ─── Component ────────────────────────────────────────────────────────────────

function VisitTimeline({
                         visits,
                         visitRecords,
                         ageMonths = null,
                         onEditPrescription,
                         onViewPrescription,
                         onCopyPrescription,
                         loading = false,
                         prescribing = false,
                       }: VisitTimelineProps) {
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const { currentUser } = useAuth();

  // Valid visits only — computed once from source, not re-derived on sort change
  const validVisits = useMemo(() =>
    visits.filter(
      (v): v is Visit & { prescription: Prescription } =>
        isValidPrescription(v.prescription)
    ),
  [visits]);

  const items = useMemo((): TimelineItem[] => {
    const rxById = new Map(validVisits.map(v => [v.prescription.id, v.prescription]));
    const placed = new Set<string>();
    const out: TimelineItem[] = [];
    for (const record of visitRecords ?? []) {
      const rxs = record.prescriptionIds.map(id => rxById.get(id)).filter(Boolean) as Prescription[];
      if (!rxs.length) {
        out.push({ kind: "visit", key: `visit-${record.id}`, date: record.date, visit: record });
        continue;
      }
      // The visit's vitals and documents show once, on its first prescription
      rxs.forEach((rx, i) => {
        placed.add(rx.id);
        out.push({ kind: "rx", key: rx.id, date: rx.date, prescription: rx, visit: i === 0 ? record : { ...record, readings: [], documents: [] } });
      });
    }
    // Prescriptions with no visit record (e.g. saved without a clinic)
    for (const v of validVisits) {
      if (!placed.has(v.prescription.id)) out.push({ kind: "rx", key: v.prescription.id, date: v.prescription.date, prescription: v.prescription });
    }
    return out;
  }, [validVisits, visitRecords]);

  const sortedItems = useMemo(() =>
    [...items].sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return sortOrder === "desc"
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    }),
  [items, sortOrder]);

  const totalPrescriptions = validVisits.length;
  const totalVisits = new Set(items.map(i => (i.kind === "visit" || i.visit ? `v:${i.visit!.id}` : `rx:${i.key}`))).size;

  // Latest prescription ID — derived from source, independent of sort order
  const latestPrescriptionId = useMemo(() => {
    if (validVisits.length === 0) return null;
    return validVisits.reduce((latest, visit) => {
      const d  = parseDate(visit.prescription.date);
      const ld = parseDate(latest.prescription.date);
      if (!d)  return latest;
      if (!ld) return visit;
      return d.getTime() > ld.getTime() ? visit : latest;
    }, validVisits[0]).prescription.id;
  }, [validVisits]);

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
        <div className="w-full">
          <div className="flex justify-between items-center mb-6">
            <div className="h-6 bg-muted rounded w-48 animate-pulse" />
            <div className="flex gap-2">
              <div className="h-8 bg-muted rounded w-20 animate-pulse" />
              <div className="h-8 bg-muted rounded w-32 animate-pulse" />
            </div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
    );
  }

  // ── Empty ───────────────────────────────────────────────────────────────────

  if (items.length === 0) {
    return (
        <div className="w-full">
          <div className="text-center py-10 medical-card">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              No prescriptions recorded
            </h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              This patient hasn't had any prescriptions yet.
            </p>
          </div>
        </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
      <div className="w-full">
        {/* Header */}
        {!prescribing && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full w-fit">
            {totalVisits} {totalVisits === 1 ? "visit" : "visits"}
            {totalPrescriptions !== totalVisits && ` · ${totalPrescriptions} ${totalPrescriptions === 1 ? "prescription" : "prescriptions"}`}
          </span>
          <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
              className="flex items-center gap-2"
          >
            <SortDesc className={cn("w-4 h-4 transition-transform", sortOrder === "asc" && "rotate-180")} />
            {sortOrder === "desc" ? "Most Recent" : "Oldest First"}
          </Button>
        </div>
        )}

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

          <div className="space-y-4 pb-6">
            {sortedItems.map((item) => {
              if (item.kind === "visit") {
                const v = item.visit;
                const date = parseDate(v.date);
                const vitals = readingItems(v.readings, ageMonths);
                return (
                    <div key={item.key} className="relative">
                      <div className="absolute left-[18px] top-[9px] w-3 h-3 rounded-full border-2 border-muted-foreground/30 bg-card z-10" />
                      <div className="ml-12 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                        <UserX className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="font-medium text-foreground">{date ? formatDate(date) : "—"}</span>
                        {v.doctorName && <span>· Dr. {v.doctorName.replace(/^dr\.?\s*/i, "")}</span>}
                        <span>· {noPrescriptionText(v)}</span>
                        {vitals.length > 0 && (
                          <span className="basis-full sm:basis-auto">
                            · Vitals: {vitals.map(x => `${x.label} ${x.text}${x.flag ? ` ${x.flag}` : ""}`).join(", ")}
                          </span>
                        )}
                        {v.invoices?.length ? (
                          <span className="basis-full sm:basis-auto">
                            · Bill: {v.invoices.length > 3 ? billSummary(v.invoices) : v.invoices.map(billText).join(", ")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                );
              }
              const visit = item;
              const isLatest  = visit.prescription.id === latestPrescriptionId;
              const canEdit   = canEditPrescription(visit.prescription, currentUser);

              return (
                  <div key={item.key} className="relative">
                    {/* Timeline marker — vertically centred with card header (~18px) */}
                    <div className={cn(
                        "absolute left-3 top-[6px] w-6 h-6 border-2 rounded-full flex items-center justify-center shadow-sm z-10 bg-card",
                        isLatest ? "border-primary" : "border-muted-foreground/30"
                    )}>
                      {isLatest
                          ? <Clock    className="w-3 h-3 text-primary" />
                          : <Calendar className="w-3 h-3 text-muted-foreground" />
                      }
                    </div>

                    {/* Visit card */}
                    <div className="ml-12">
                      <VisitCard
                          prescription={visit.prescription}
                          // Only on the day it was written, by the doctor who wrote it
                          onEdit={canEdit && onEditPrescription
                              ? onEditPrescription
                              : undefined
                          }
                          onView={onViewPrescription}
                          onCopy={onCopyPrescription}
                          isLatest={isLatest}
                          visit={visit.visit}
                          ageMonths={ageMonths}
                          prescribing={prescribing}
                      />
                    </div>
                  </div>
              );
            })}
          </div>

          {/* Timeline end marker */}
          <div className="absolute left-3 bottom-0 w-6 h-6 bg-muted border-2 border-border rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full" />
          </div>
        </div>
      </div>
  );
}

export default memo(VisitTimeline);